import { useEffect, useRef, useState, type ReactNode } from "react";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { Button } from "../../ui/Button";
import {
  closeFind,
  getFindState,
  replaceAll,
  replaceCurrent,
  setFind,
  stepFind,
} from "../plugins/find-replace";

export interface FindReplacePanelProps {
  view: EditorView;
  state: EditorState;
  readOnly?: boolean;
}

/**
 * Find and replace (`editor.md` §11): incremental search with case and regular
 * expression toggles, wrapping next/previous, an "n of m" count, and replace
 * one or all. Escape closes the panel and clears its decorations.
 */
export function FindReplacePanel({
  view,
  state,
  readOnly = false,
}: FindReplacePanelProps): ReactNode {
  const find = getFindState(state);
  const [replacement, setReplacement] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (find.open) inputRef.current?.focus();
  }, [find.open]);

  if (!find.open) return null;
  const count = find.matches.length;
  const position = count === 0 ? 0 : find.index + 1;

  return (
    <div className="wiki-find" role="search" aria-label="Find and replace" data-testid="find-panel">
      <input
        ref={inputRef}
        className="wiki-menu__input"
        aria-label="Find"
        value={find.query}
        placeholder="Find"
        onChange={(event) => {
          setFind(view, { query: event.target.value });
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          stepFind(view, event.shiftKey ? -1 : 1);
        }}
      />
      <label className="wiki-find__option">
        <input
          type="checkbox"
          checked={find.caseSensitive}
          onChange={(event) => {
            setFind(view, { caseSensitive: event.target.checked });
          }}
        />
        Match case
      </label>
      <label className="wiki-find__option">
        <input
          type="checkbox"
          checked={find.regex}
          onChange={(event) => {
            setFind(view, { regex: event.target.checked });
          }}
        />
        Regular expression
      </label>
      <span
        className="wiki-find__count"
        data-testid="find-count"
      >{`${String(position)} of ${String(count)}`}</span>
      <Button
        size="sm"
        onClick={() => {
          stepFind(view, -1);
        }}
      >
        Previous
      </Button>
      <Button
        size="sm"
        onClick={() => {
          stepFind(view, 1);
        }}
      >
        Next
      </Button>
      {!readOnly && (
        <>
          <input
            className="wiki-menu__input"
            aria-label="Replace with"
            placeholder="Replace"
            value={replacement}
            onChange={(event) => {
              setReplacement(event.target.value);
            }}
          />
          <Button
            size="sm"
            onClick={() => {
              replaceCurrent(view, replacement);
            }}
          >
            Replace
          </Button>
          <Button
            size="sm"
            onClick={() => {
              replaceAll(view, replacement);
            }}
          >
            Replace all
          </Button>
        </>
      )}
      <Button
        size="sm"
        variant="ghost"
        aria-label="Close find"
        onClick={() => {
          closeFind(view);
          view.focus();
        }}
      >
        ✕
      </Button>
    </div>
  );
}
